import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { createSelector } from "reselect";
import { matchPath, useLocation } from "react-router-dom";
import qs from "query-string";
import debounce from "lodash/debounce";
import isEqual from "lodash/isEqual";

import { routes } from "../../constants/routes";
import { ATTRIBUTION_PARAMS } from "../../constants/site";
import { SITE_SHOW_OPTIONS, SITE_SORT_OPTIONS } from "../../constants/search";
import { parseRangeQuery } from "../../helpers/format";
import getBreadcrumbs from "./helpers/get-breadcrumbs";
import getSearchTerms from "./helpers/get-search-terms";
import history from "../../helpers/history";
import { getRangeLimit } from "./Search.helpers";
import useCategoryNames from "../categories/hooks/use-category-names";

/**
 * Use query hook
 * * Manages all search query state
 * * Manages all search interactions with router
 * * Compiles breadcrumb data
 *
 * @returns object
 */
export default function useQuery() {
  let { pathname, search: locationSearch } = useLocation();
  const authenticated =
    useSelector(state => !!state.login.auth.expires) || false;
  const sliderDefaults = useSelector(selectSliderDefaults);

  // Ignore analytics parameters in location search
  const locationSearchWithoutAnalytics = useMemo(
    () => stripAnalyticsQuery(locationSearch),
    [locationSearch]
  );

  // Initialize state except for main query
  const activeQueryString = useRef(locationSearchWithoutAnalytics);
  const [baseQueryString, setBaseQueryString] = useState("");
  const [breadcrumbsFind, setBreadcrumbsFind] = useState([]);
  const [breadcrumbsUser, setBreadcrumbsUser] = useState([]);
  const [breadcrumbsVisible, setBreadcrumbsVisible] = useState([]);
  const [formType, setFormType] = useState("all");
  const [hasQuery, setHasQuery] = useState(false);
  const [queryAlert, setQueryAlert] = useState("");
  const [queryFull, setQueryFull] = useState(); // should be undefined on route change
  const resultsPath = useMemo(
    () => getResultsPath(pathname, hasQuery),
    [pathname, hasQuery]
  );

  useEffect(() => {
    setQueryFull(undefined);

    if (!isSearch(pathname)) {
      setFormType("all");
    }
  }, [pathname]);

  // Get external inputs that impact main query
  const [baseQuery, externalQueryInputs] = useMemo(
    () =>
      getExternalQueryInputs(
        authenticated,
        baseQueryString,
        locationSearchWithoutAnalytics,
        sliderDefaults,
        pathname // only here to trigger re-evaluation on route change
      ),
    [
      authenticated,
      baseQueryString,
      locationSearchWithoutAnalytics,
      pathname,
      sliderDefaults,
    ]
  );

  // Handle changes to main query
  const [query, setQuery] = useReducer(queryReducer, queryInit);

  useEffect(() => {
    if (isSearch(pathname)) {
      setQuery({ type: "external", payload: externalQueryInputs });
    }
  }, [externalQueryInputs, pathname]);

  // Exported method for updating main query from search forms
  // `type` should be a field name or `reset`
  // leave `payload` empty to reset to initial state w/ base query
  const updateQuery = useCallback(
    (type, payload) => {
      if (type === "reset" && !!baseQuery) {
        payload = JSON.parse(baseQuery);
      }

      // Prevent mobile search from specials page from
      // firing useless search request
      if (
        pathname === routes.specials.path &&
        !["batch", "limit", "page", "reset", "sort"].includes(type)
      ) {
        setQuery({
          type: "batch",
          payload: { salePrice: undefined, [type]: payload },
        });
      }

      setQuery({ type, payload });
    },
    [baseQuery, pathname]
  );

  // Prepare location change
  // Debounced so as to not to spam the API server
  const getCategoryNames = useCategoryNames(); // Gives `getBreadcrumbs` access to the redux store
  const prepareLocationChange = useMemo(
    () =>
      debounce((authenticated, baseQuery, formType, query, sliderDefaults) => {
        const [
          userDefinedQuery,
          updatedQueryString,
          updatedQueryAlert,
          updatedQueryFull,
          updatedHasQuery,
        ] = getNewQuery(authenticated, baseQuery, query, sliderDefaults);

        const [breadcrumbsFind, breadcrumbsUser, breadcrumbsVisible] =
          getBreadcrumbs(
            userDefinedQuery,
            baseQuery,
            sliderDefaults,
            formType,
            authenticated,
            getCategoryNames
          );
        setBreadcrumbsFind(breadcrumbsFind);
        setBreadcrumbsUser(breadcrumbsUser);
        setBreadcrumbsVisible(breadcrumbsVisible);
        setHasQuery(updatedHasQuery);
        setNewQueryString(updatedQueryString);
        setQueryAlert(updatedQueryAlert);
        setQueryFull(updatedQueryFull);
      }, 1000),
    [getCategoryNames]
  );

  useEffect(() => {
    prepareLocationChange(
      authenticated,
      baseQuery,
      formType,
      query,
      sliderDefaults
    );
    return () => prepareLocationChange.cancel();
  }, [
    authenticated,
    baseQuery,
    formType,
    pathname, // re-evaluate query on pathname change so that back/forward nav works properly
    prepareLocationChange,
    query,
    sliderDefaults,
  ]);

  // Make location changes
  // Not debounced becaused it creates timing issues in production builds
  const [newQueryString, setNewQueryString] = useState(
    locationSearchWithoutAnalytics
  );

  useEffect(() => {
    if (activeQueryString.current !== newQueryString) {
      activeQueryString.current = newQueryString;
      history.push({
        pathname: resultsPath,
        search: newQueryString,
      });
    }
  }, [newQueryString, resultsPath]);

  // Deeply memoize active query to avoid unneccessary
  // re-renders in search context consumers
  const activeQuery = useRef({});
  const nextQuery = {
    breadcrumbsFind,
    breadcrumbsUser,
    breadcrumbsVisible,
    formType,
    hasQuery: hasQuery,
    queryAll: query,
    queryAlert,
    queryFull,
    setBaseQueryString,
    setFormType,
    sliderDefaults,
    updateQuery,
  };

  if (!isEqual(activeQuery.current, nextQuery)) {
    activeQuery.current = nextQuery;
  }

  // Return active query object
  return activeQuery.current;
}

/* ---------------------------------------- */

/**
 * Initial query values
 */
const queryInit = {
  accountNumber: [],
  bodyType: [],
  cabType: [],
  category_id: [],
  engineMake: [],
  distance: "",
  frontAxleRating: undefined,
  grossVehicleWeight: undefined,
  horsepower: undefined,
  hasCabExtenders: false,
  isCabover: false,
  locationZip: "",
  limit: SITE_SHOW_OPTIONS[0].value,
  make: [],
  meter: undefined,
  model: [],
  numberOfRearAxles: [],
  page: "1",
  price: undefined,
  rearAxleRating: undefined,
  salePrice: undefined,
  search: [],
  sleeperNumberOfBeds: [],
  sleeperSize: undefined,
  sort: SITE_SORT_OPTIONS[0].value,
  sourceDivision: [],
  sourceType: "all",
  status: [],
  transmissionSpeed: undefined,
  transmissionType: [],
  unitModelClass: [],
  visibility: "",
  wheelbase: undefined,
  year: undefined,
};

/**
 * Manages query state
 *
 * @param {object} state
 * @param {mixed} action
 * @returns {object}
 */
function queryReducer(state, action) {
  if (action.type === "external") {
    const externalUpdate = { ...queryInit, ...action.payload };
    return !isEqual(state, externalUpdate) ? externalUpdate : state;
  } else if (action.type === "batch") {
    const batchUpdate = { ...state, ...action.payload };
    return !isEqual(state, batchUpdate) ? batchUpdate : state;
  } else if (action.type === "reset") {
    return { ...queryInit, ...action.payload }; // payload should be the base query
  } else if (queryInit.hasOwnProperty(action.type)) {
    const singleUpdate = {
      ...state,
      [action.type]: action.payload || queryInit[action.type],
    };
    return !isEqual(state, singleUpdate) ? singleUpdate : state;
  } else {
    throw new Error(`Unknown query type: ${action.type}`);
  }
}

/**
 * Get search results path
 * Search is available from every page in the mobile UI, so
 * determine if the request should be handled on the current page
 * or redirected to the all listings page
 *
 * @param {string} pathname
 * @param {bool} hasQuery
 * @returns {string}
 */
function getResultsPath(pathname, hasQuery) {
  // Only allow page and sort query updates for the specials page
  if (
    !hasQuery &&
    matchPath(pathname, { path: routes.specials.path, exact: false })
  ) {
    return pathname;
  }

  // Otherwise, allow secondary search pages or redirect to all listings
  return matchPath(pathname, {
    path: [
      routes.campaignPage.path,
      routes.compile.path,
      routes.landingPage.path,
      routes.watch.path,
    ],
    exact: false,
  })
    ? pathname
    : routes.results.path;
}

/**
 * Memoized slider defaults object retrieved from Redux store
 */
const selectSliderDefaults = createSelector(
  state => state.searchCurrentMeta,
  ({
    frontAxleRating,
    grossVehicleWeight,
    horsepower,
    meter,
    price,
    rearAxleRating,
    sleeperSize,
    transmissionSpeed,
    wheelbase,
    year,
  }) => ({
    frontAxleRating,
    grossVehicleWeight,
    horsepower,
    meter,
    price,
    rearAxleRating,
    sleeperSize,
    transmissionSpeed,
    wheelbase,
    year,
  })
);

/**
 * Merge external inputs into query
 * @param {bool} authenticated
 * @param {string} baseQueryString
 * @param {string} locationSearch
 * @param {object} sliderDefaults
 * @param {string} pathname
 * @returns {object}
 */
function getExternalQueryInputs(
  authenticated,
  baseQueryString,
  locationSearch,
  sliderDefaults,
  pathname
) {
  let externalQueryInputs = [];

  // Spreading prototypeless object returned by qs.parse to add hasOwnProperty method
  let baseQuery = { ...qs.parse(baseQueryString, { parseNumbers: true }) };
  const locationQuery = { ...qs.parse(locationSearch, { parseNumbers: true }) };

  Object.entries(baseQuery).forEach(([key, value]) => {
    const formatted = formatRawQuery(key, value, authenticated, sliderDefaults);
    baseQuery[key] = formatted;

    if (queryInit.hasOwnProperty(key) && typeof formatted !== "undefined") {
      externalQueryInputs[key] = formatted;
    }
  });

  // Saving as a string to avoid shallow equality useEffect problems
  baseQuery = JSON.stringify(baseQuery);

  // Uncomment when/if API is updated to not return user input as default values
  //
  // For example, user input range 1998 or newer (?year=1998%2C-1)
  // 1) before user input—limit = [0, 2023], defaults = [1992, 2023]
  // 2) before user input—limit = [0, 2023], defaults = [1998, 2023]
  //
  // This makes it impossible for the frontend to reliably differentiate
  // between user input and system defaults and causes a redirect loop
  //
  // Object.entries(sliderDefaults).forEach(([key, value]) => {
  //   if (value) {
  //     const formatted = formatRawQuery(
  //       key,
  //       value,
  //       authenticated,
  //       sliderDefaults
  //     );
  //     if (queryInit.hasOwnProperty(key) && typeof formatted !== "undefined") {
  //       externalQueryInputs[key] = formatted;
  //     }
  //   }
  // });

  Object.entries(locationQuery).forEach(([key, value]) => {
    const formatted = formatRawQuery(key, value, authenticated, sliderDefaults);

    if (queryInit.hasOwnProperty(key) && typeof formatted !== "undefined") {
      externalQueryInputs[key] = formatted;
    }
  });

  // Meter queries are unreliable when meterType is undefined, so delete it
  if (!!externalQueryInputs.meter && !sliderDefaults?.meter?.type) {
    delete externalQueryInputs.meter;
  }

  return [baseQuery, externalQueryInputs];
}

/** Test if pathname is a searchable page */
function isSearch(pathname) {
  return matchPath(pathname, {
    path: [
      routes.campaignPage.path,
      routes.compile.path,
      routes.landingPage.path,
      routes.results.path,
      routes.specials.path,
      routes.watch.path,
    ],
    exact: false,
  });
}

/**
 * Format raw query values
 * @param {string} key
 * @param {mixed} value
 * @param {bool} authenticated
 * @param {object} sliderDefaults
 * @returns {object}
 */
function formatRawQuery(key, value, authenticated, sliderDefaults = {}) {
  switch (key) {
    case "distance":
    case "limit":
    case "page":
      return !isNaN(value) ? `${value}` : undefined;
    case "frontAxleRating":
    case "grossVehicleWeight":
    case "horsepower":
    case "meter":
    case "price":
    case "rearAxleRating":
    case "sleeperSize":
    case "transmissionSpeed":
    case "wheelbase":
    case "year":
      return getSliderRange(sliderDefaults[key], value);
    case "accountNumber":
    case "model":
      return (
        (!!value && [...new Set(getSearchTerms(value, "AND", false))]) ||
        undefined
      );
    case "search":
      return (
        (!!value && [...new Set(getSearchTerms(value, "OR", false))]) ||
        undefined
      );
    case "bodyType":
    case "cabType":
    case "category_id":
    case "engineMake":
    case "make":
    case "sourceDivision":
    case "status":
    case "transmissionType":
    case "unitModelClass":
      return (!!value && `${value}`.split(",")) || undefined;
    case "numberOfRearAxles":
    case "sleeperNumberOfBeds":
      const numberList = !!value
        ? `${value}`
            .split(",")
            .map(v => parseInt(v, 10).toString())
            .filter(v => !isNaN(v))
        : [];
      return !!numberList.length ? numberList : undefined;
    case "hasCabExtenders":
    case "isCabover":
      return !value || value === "false" || value === "0" ? false : true;
    case "visibility":
      return authenticated ? value : undefined;
    default:
      return `${value}`;
  }
}

/**
 * Gets array of slider range values
 *
 * @param {object} defaults
 * @param {array} values
 * @returns {array|undefined}
 */
function getSliderRange(defaults = {}, values) {
  const { defaultValues, min, max } = defaults;
  let [low, high] = parseRangeQuery(values);

  if (!!defaultValues) {
    low = getSliderValue(low, defaultValues[0], min, max);
    high = getSliderValue(high, defaultValues[1], min, max);
  }

  return (!!low || low === 0) && !!high ? [low, high] : undefined;
}

/**
 * Get single slider range value
 *
 * @param {number} queryValue
 * @param {number} defaultValue
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
function getSliderValue(queryValue, defaultValue, min, max) {
  if (queryValue === -1) {
    return max;
  } else if (
    !isNaN(queryValue) &&
    (!!queryValue || queryValue === 0) &&
    queryValue >= min &&
    queryValue <= max
  ) {
    return queryValue;
  } else {
    return defaultValue;
  }
}

/**
 * Get new API query request
 *
 * @param {bool} authenticated
 * @param {object} baseQuery
 * @param {object} query
 * @param {object} sliderDefaults
 * @returns {array}
 */
function getNewQuery(
  authenticated,
  baseQuery = {},
  query = {},
  sliderDefaults = {}
) {
  baseQuery = JSON.parse(baseQuery);

  const newQuery = Object.entries(query).reduce(
    (nq, [key, value]) => {
      const isAlertable = ![
        "limit",
        "meterType",
        "page",
        "salePrice", // Only used for specials base query
        "sort",
      ].includes(key);
      const isBase = baseQuery.hasOwnProperty(key);
      const isBaseOverride = isBase && !isEqual(baseQuery[key], value);
      const isNotBase = !isBase || isBaseOverride;

      const formattedValue =
        value !== queryInit[key] &&
        formatNewQueryValue(key, value, authenticated, sliderDefaults);

      if (!!formattedValue || formattedValue === 0) {
        nq.full[key] = formattedValue;

        if (isAlertable) {
          nq.alert[key] = formattedValue;
        }

        if (isNotBase) {
          nq.user[key] = value;
          nq.userString[key] = formattedValue;
        }

        if (!nq.hasQuery && isAlertable && isNotBase) {
          nq.hasQuery = true;
        }
      }

      return nq;
    },
    { alert: {}, hasQuery: false, full: {}, user: {}, userString: {} }
  );

  const newQueryString = qs.stringifyUrl(
    {
      url: "",
      query: newQuery.userString,
    },
    { sort: sortPageParamLast }
  );
  const newQueryAlert = qs.stringifyUrl(
    {
      url: "",
      query: newQuery.alert,
    },
    { sort: sortPageParamLast }
  );
  const newQueryFull = qs.stringifyUrl(
    {
      url: "",
      query: newQuery.full,
    },
    { sort: sortPageParamLast }
  );

  return [
    newQuery.user,
    newQueryString,
    newQueryAlert,
    newQueryFull,
    newQuery.hasQuery,
  ];
}

/**
 * Move page parameter to end of query string
 * instead of alphabetizing
 *
 * @param {string} a
 * @param {string} b
 * @returns {bool|number}
 */
function sortPageParamLast(a, b) {
  if (a === "page") {
    return 1;
  } else if (b === "page") {
    return -1;
  } else {
    return a.localeCompare(b);
  }
}

/**
 * Format value for new query string
 * @param {string} key
 * @param {*} value
 * @param {bool} authenticated
 * @param {object} sliderDefaults
 * @returns {mixed}
 */
function formatNewQueryValue(key, value, authenticated, sliderDefaults = {}) {
  switch (key) {
    case "distance":
    case "limit":
    case "page":
      const int = parseInt(value, 10);
      return !isNaN(int) ? int : undefined;
    case "frontAxleRating":
    case "grossVehicleWeight":
    case "horsepower":
    case "meter":
    case "price":
    case "rearAxleRating":
    case "sleeperSize":
    case "transmissionSpeed":
    case "wheelbase":
    case "year":
      return formatSliderRange(key, sliderDefaults[key], value);
    case "accountNumber":
    case "model":
      return (Array.isArray(value) && value.join(" OR ")) || undefined;
    case "search":
      return (Array.isArray(value) && value.join(" AND ")) || undefined;
    case "bodyType":
    case "cabType":
    case "category_id":
    case "engineMake":
    case "make":
    case "numberOfRearAxles":
    case "sleeperNumberOfBeds":
    case "sourceDivision":
    case "status":
    case "transmissionType":
    case "unitModelClass":
      return (Array.isArray(value) && value.join(",")) || undefined;
    case "hasCabExtenders":
    case "isCabover":
      return !!value ? "true" : undefined;
    case "visibility":
      return authenticated ? value : undefined;
    default:
      return value;
  }
}

/**
 * Format slider range
 *
 * @param {string} key
 * @param {object} defaults
 * @param {array} values
 * @returns {string}
 */
function formatSliderRange(key, defaults = {}, values) {
  const { max, min /* , defaultValues */ } = defaults;

  if (!values || !Array.isArray(values) || values.some(v => isNaN(v))) {
    return undefined;
  }

  // Uncomment when/if API is updated to not return user input as default values
  //
  // For example, user input range 1998 or newer (?year=1998%2C-1)
  // 1) before user input—limit = [0, 2023], defaults = [1992, 2023]
  // 2) before user input—limit = [0, 2023], defaults = [1998, 2023]
  //
  // This makes it impossible for the frontend to reliably differentiate
  // between user input and system defaults and causes a redirect loop
  //
  // if (isEqual(values, defaultValues)) {
  //   return undefined;
  // }

  if (values[1] === max || getRangeLimit(values[1], key, "max") === max) {
    values[1] = -1;
  }

  if (
    /*(!!defaultValues && values[0] === defaultValues[0]) ||*/
    (values[0] === min || getRangeLimit(values[0], key, "min") === min) &&
    values[1] === -1
  ) {
    return undefined;
  }

  return values.join(",");
}

/**
 * Strip analytics query
 *
 * @param {string} locationSearch
 * @returns {string}
 */
function stripAnalyticsQuery(locationSearch) {
  const withoutAnalytics = qs.exclude(locationSearch, ATTRIBUTION_PARAMS);

  return withoutAnalytics;
}
